import React from 'react'
import { OptionOnSelectData, SelectionEvents } from '@fluentui/react-combobox'
import { Field, Dropdown, Option } from '@fluentui/react-components'
import { Control, Controller, Validate } from 'react-hook-form'
import { DropdownOption } from '../../../helpers/enumHelper'
import { t } from '../../../i18n/i18n'

type DropdownProps = {
   label?: string
   options: DropdownOption[]
   ariaLabel?: string
   selectedOptions?: string[]
   disabled?: boolean
   onOptionSelect?: (event: SelectionEvents, data: OptionOnSelectData) => void
   multiselect: boolean
   className?: string
}
export const BasicDropdown = ({ label, options, ariaLabel, selectedOptions, disabled, onOptionSelect, multiselect, className }: DropdownProps) => {
   if (label) {
      return (
         <Field label={label} className={className ?? ''}>
            <Dropdown
               multiselect={multiselect}
               disabled={disabled ?? false}
               aria-label={ariaLabel}
               value={selectedOptions.length > 0 ? selectedOptions.join(', ') : ''}
               selectedOptions={selectedOptions}
               onOptionSelect={onOptionSelect}
               style={{ minWidth: 'auto', width: '100%' }}
            >
               {options.map((x) => (
                  <Option key={x.key} value={x.key.toString()} disabled={x.disabled ? x.disabled : false}>
                     {x.text}
                  </Option>
               ))}
            </Dropdown>
         </Field>
      )
   }

   return (
      <Dropdown
         multiselect={multiselect}
         disabled={disabled ?? false}
         aria-label={ariaLabel}
         value={selectedOptions.length > 0 ? selectedOptions.join(', ') : ''}
         selectedOptions={selectedOptions}
         onOptionSelect={onOptionSelect}
         className={className ?? ''}
         style={{ minWidth: 'auto', width: '100%' }}
      >
         {options.map((x) => (
            <Option key={x.key} value={x.key.toString()} disabled={x.disabled ? x.disabled : false}>
               {x.text}
            </Option>
         ))}
      </Dropdown>
   )
}
type ControlledDropdownProps = {
   name: any
   control: Control<any>
   label: string
   options: DropdownOption[]
   disabled?: boolean
   required?: boolean
   multi?: boolean
   errorMessage?: string
   validate?: Validate<any, any> | Record<string, Validate<any, any>>
}
export const ControlledDropDown = ({ name, control, label, options, disabled, multi, required, errorMessage, validate }: ControlledDropdownProps) => {
   return (
      <Controller
         name={name}
         control={control}
         rules={{ required: required ? t('RequiredField') : false, validate: validate }}
         render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Field
               label={label}
               required={required}
               validationMessage={error ? error.message : errorMessage ? errorMessage : null}
               validationState={error || errorMessage ? 'error' : null}
            >
               <Dropdown
                  multiselect={multi ?? false}
                  value={
                     multi
                        ? value
                             ?.map((v: any) => options.find((x) => x.key === v)?.text)
                             .filter(Boolean)
                             .join(', ') ?? ''
                        : options.find((x) => x.key === value)?.text ?? ''
                  }
                  onOptionSelect={(e, d) => {
                     if (multi) {
                        const selected = d.selectedOptions.map((x) => Number(x))
                        onChange(selected)
                     } else {
                        onChange(d.optionValue ? Number(d.optionValue) : '')
                     }
                  }}
                  selectedOptions={multi ? value?.map(String) ?? [] : value ? [String(value)] : []}
                  disabled={disabled ?? false}
                  style={{ minWidth: 'auto' }}
               >
                  {options.map((x) => (
                     <Option key={x.key} value={x.key.toString()} disabled={x.disabled ? x.disabled : false}>
                        {x.text}
                     </Option>
                  ))}
               </Dropdown>
            </Field>
         )}
      />
   )
}

export const ControlledDropDownMulti = ({ name, control, label, options, disabled, required, errorMessage, validate }: ControlledDropdownProps) => {
   const ALL_VALUE = 'SELECT_ALL'
   const internalOptions = [{ key: ALL_VALUE, text: t('All') }, ...options]

   return (
      <Controller
         name={name}
         control={control}
         rules={{ required: required ? t('RequiredField') : false, validate: validate }}
         render={({ field: { value = [], onChange }, fieldState: { error } }) => {
            const isAllSelected = value?.length === options?.length && options?.length > 0

            const handleSelect = (e: SelectionEvents, d: OptionOnSelectData) => {
               if (d.optionValue === ALL_VALUE) {
                  if (isAllSelected) {
                     onChange([])
                  } else {
                     onChange(options.map((opt) => opt.key))
                  }
               } else {
                  const newSelection = d.selectedOptions.filter((v) => v !== ALL_VALUE).map((v) => (isNaN(Number(v)) ? v : Number(v)))

                  onChange(newSelection)
               }
            }

            return (
               <Field
                  label={label}
                  required={required}
                  validationMessage={error ? error.message : errorMessage ?? null}
                  validationState={error || errorMessage ? 'error' : null}
               >
                  <Dropdown
                     multiselect={true}
                     value={
                        isAllSelected
                           ? t('All')
                           : value
                                ?.map((v: any) => options.find((x) => String(x.key) === String(v))?.text)
                                .filter(Boolean)
                                .join(', ')
                     }
                     onOptionSelect={handleSelect}
                     selectedOptions={isAllSelected ? [ALL_VALUE, ...value?.map(String)] : value?.map(String)}
                     disabled={disabled ?? false}
                     style={{ minWidth: 'auto', width: '100%' }}
                  >
                     {internalOptions.map((x) => (
                        <Option key={x.key} value={x.key.toString()} disabled={x.disabled}>
                           {x.text}
                        </Option>
                     ))}
                  </Dropdown>
               </Field>
            )
         }}
      />
   )
}

export const GuidMultiSelectDropdown = ({ name, control, label, options, disabled, required }: ControlledDropdownProps) => {
   return (
      <Controller
         name={name}
         control={control}
         rules={{ required: required ? t('RequiredField') : false }}
         render={({ field: { value, onChange }, fieldState: { error } }) => {
            let currentArray: string[] = []
            if (!value) currentArray = []
            else if (Array.isArray(value)) currentArray = value
            else {
               try {
                  currentArray = JSON.parse(value)
               } catch (e) {
                  currentArray = []
               }
            }

            return (
               <Field label={label} required={required} validationState={error ? 'error' : null} validationMessage={error?.message}>
                  <Dropdown
                     multiselect={true}
                     value={currentArray
                        .map((v) => options.find((x) => x.key === v)?.text)
                        .filter(Boolean)
                        .join(', ')}
                     selectedOptions={currentArray}
                     onOptionSelect={(_, d) => {
                        // We store it back as a JSON string to match the C# string property
                        onChange(JSON.stringify(d.selectedOptions))
                     }}
                     disabled={disabled}
                     style={{ minWidth: 'auto', width: '100%' }}
                  >
                     {options.map((x) => (
                        <Option key={x.key} value={x.key.toString()}>
                           {x.text}
                        </Option>
                     ))}
                  </Dropdown>
               </Field>
            )
         }}
      />
   )
}
